import React, {useEffect, useCallback, useMemo, memo, Fragment} from 'react';
import {Props} from './ac-pagination.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {ICONS, KEYS, SIZES, THEMES} from '@asdf-constants';

// Imports => Utilities
import {AcUUID, AcIsSet, AcIsFunction} from '@asdf-utils';

// Imports => Hooks
import {usePagination} from '@asdf-hooks/use-pagination.hook';

// Imports => Atoms
import {AcIcon, AcRipple} from '@asdf-atoms';

const _CLASSES = {
  MAIN: 'ac-pagination',
  FROM_TO: 'ac-pagination__from-to',
  NAV: 'ac-pagination__nav',
  LIST: 'ac-pagination__list',
  ITEM: 'ac-pagination__list__item',
  LINK: {
    MAIN: 'ac-pagination__link',
    ACTIVE: 'ac-pagination__link--active',
    TRANSPARENT: 'ac-pagination__link--transparent',
    DISABLED: 'ac-pagination__link--disabled',
  },
  ELLIPSIS: 'ac-pagination__ellipsis',
};

const AcPagination = memo(
  ({
    current_page = 0,
    last_page = 0,
    total_count = 0,
    total_pages = 0,
    per_page = 0,
    siblings = 1,
    from,
    to,
    showFromTo = false,
    callback,
    className,
    translations = {
      'go-to-first-page': 'Go to first page',
      'go-to-previous-page': 'Go to previous page',
      'go-to-next-page': 'Go to next page',
      'go-to-last-page': 'Go to last page',
      'go-to-page': 'Go to page',
      page: 'page',
      'current-page': 'current page',
      'pagination-navigation': 'pagination navigation',
      showing: 'showing',
      of: 'of',
      results: 'results',
    },
    ...rest
  }) => {
    const range = usePagination({
      total_count,
      total_pages,
      per_page,
      siblings,
      current_page,
    });

    const handlePaginationItemClick = (event, num) => {
      if (event.preventDefault) event.preventDefault();
      if (event.stopPropagation) event.stopPropagation();

      if (num === current_page) return;
      if (num < 1 || num > last_page) return;

      if (AcIsSet(callback) && AcIsFunction(callback)) {
        callback(event, num);
      }
    };

    const handleKeyUp = event => {
      if (!AcIsSet(event)) return;
      if (event && event.persist) event.persist();
      const key = event.key || event.which;

      const $active_element = document.activeElement;

      const $inputs = ['input', 'select', 'button', 'textarea', 'form'];

      if (key) {
        if ($active_element && $inputs.indexOf($active_element.tagName.toLowerCase()) === -1) {
          switch (key) {
            case 'ArrowLeft':
            case 37:
              handlePaginationItemClick(event, current_page - 1);
              break;

            case 'ArrowRight':
            case 39:
              handlePaginationItemClick(event, current_page + 1);
              break;

            default:
          }
        }
      }
    };

    const addEvents = () => {
      document.addEventListener('keyup', handleKeyUp, false);
    };

    const removeEvents = () => {
      document.removeEventListener('keyup', handleKeyUp, false);
    };

    useEffect(() => {
      addEvents();

      return () => removeEvents();
    }, []);

    const getEllipsisClassNames = useMemo(() => {
      return clsx(_CLASSES.ELLIPSIS);
    }, []);

    const getLinkClassNames = useCallback(
      (active = false, disabled = false, transparent = false) => {
        return clsx(
          _CLASSES.LINK.MAIN,
          active && _CLASSES.LINK.ACTIVE,
          disabled && _CLASSES.LINK.DISABLED,
          transparent && _CLASSES.LINK.TRANSPARENT
        );
      },
      []
    );

    const getListItemClassNames = useMemo(() => {
      return clsx(_CLASSES.ITEM);
    }, []);

    const getListClassNames = useMemo(() => {
      return clsx(_CLASSES.LIST);
    }, []);

    const getNavClassNames = useMemo(() => {
      return clsx(_CLASSES.NAV);
    }, []);

    const getFromToClassNames = useMemo(() => {
      return clsx(_CLASSES.FROM_TO);
    }, []);

    const getMainClassNames = useMemo(() => {
      return clsx(_CLASSES.MAIN, className);
    }, [className]);

    const renderFirstPageItem = useMemo(() => {
      const disabled = current_page === 1;

      const props = {
        href: '#',
        onClick: disabled ? null : event => handlePaginationItemClick(event, 1),
        className: getLinkClassNames(false, disabled, true),
        'aria-label': translations['go-to-first-page'],
        title: translations['go-to-first-page'],
        'aria-disabled': disabled,
        disabled,
      };

      const object = (
        <li
          className={`${getListItemClassNames} h-hidden-xxs h-hidden-xs`}
          rel={`ac-pagination__item--first`}
        >
          <a {...props}>
            {!disabled && <AcRipple theme={THEMES.PITCH} size={SIZES.SMALL} simple />}
            <AcIcon icon={ICONS.CHEVRON_LEFT_START} alt={props.title} title={props.title} />
          </a>
        </li>
      );

      return object;
    }, [handlePaginationItemClick, getLinkClassNames, getListItemClassNames, current_page]);

    const renderPreviousPageItem = useMemo(() => {
      const disabled = current_page === 1;

      const props = {
        href: '#',
        onClick: disabled ? null : event => handlePaginationItemClick(event, current_page - 1),
        className: getLinkClassNames(false, disabled, true),
        'aria-label': translations['go-to-previous-page'],
        title: translations['go-to-previous-page'],
        'aria-disabled': disabled,
        disabled,
      };

      const object = (
        <li className={`${getListItemClassNames}`} rel={`ac-pagination__item--previous`}>
          <a {...props}>
            {!disabled && <AcRipple theme={THEMES.PITCH} size={SIZES.SMALL} simple />}
            <AcIcon icon={ICONS.CHEVRON_LEFT} alt={props.title} title={props.title} />
          </a>
        </li>
      );

      return object;
    }, [
      handlePaginationItemClick,
      getLinkClassNames,
      getListItemClassNames,
      current_page,
      last_page,
    ]);

    const renderNextPageItem = useMemo(() => {
      const disabled = current_page === last_page;

      const props = {
        href: '#',
        onClick: disabled ? null : event => handlePaginationItemClick(event, current_page + 1),
        className: getLinkClassNames(false, disabled, true),
        'aria-label': translations['go-to-next-page'],
        title: translations['go-to-next-page'],
        'aria-disabled': disabled,
        disabled,
      };

      const object = (
        <li className={`${getListItemClassNames}`} rel={`ac-pagination__item--next`}>
          <a {...props}>
            {!disabled && <AcRipple theme={THEMES.PITCH} size={SIZES.SMALL} simple />}
            <AcIcon icon={ICONS.CHEVRON_RIGHT} alt={props.title} title={props.title} />
          </a>
        </li>
      );

      return object;
    }, [
      handlePaginationItemClick,
      getLinkClassNames,
      getListItemClassNames,
      current_page,
      last_page,
    ]);

    const renderLastPageItem = useMemo(() => {
      const disabled = current_page === last_page;

      const props = {
        href: '#',
        onClick: event => handlePaginationItemClick(event, last_page),
        className: getLinkClassNames(false, disabled, true),
        'aria-label': translations['go-to-last-page'],
        title: translations['go-to-last-page'],
        'aria-disabled': disabled,
        disabled,
      };

      const object = (
        <li
          className={`${getListItemClassNames} h-hidden-xxs h-hidden-xs`}
          rel={`ac-pagination__item--last`}
        >
          <a {...props}>
            {!disabled && <AcRipple theme={THEMES.PITCH} size={SIZES.SMALL} simple />}
            <AcIcon icon={ICONS.CHEVRON_RIGHT_END} alt={props.title} title={props.title} />
          </a>
        </li>
      );

      return object;
    }, [
      handlePaginationItemClick,
      getLinkClassNames,
      getListItemClassNames,
      current_page,
      last_page,
    ]);

    const renderEllipsis = useMemo(() => {
      const object = (
        <li className={getListItemClassNames} rel={`ac-pagination__item--ellipsis`}>
          <AcIcon
            icon={ICONS.DOTS_HORIZONTAL}
            alt={''}
            title={''}
            className={getEllipsisClassNames}
          />
        </li>
      );

      return object;
    }, [getLinkClassNames, getEllipsisClassNames]);

    const renderPaginationItems = useMemo(() => {
      if (!AcIsSet(total_count)) return null;
      if (!AcIsSet(current_page)) return null;
      if (!AcIsSet(range)) return null;

      const collection = range;
      const len = collection.length;
      let n = 0;
      let result = [];

      for (n; n < len; n++) {
        const item = collection[n];

        if (item === KEYS.DOTS) {
          result.push(<Fragment key={AcUUID()}>{renderEllipsis}</Fragment>);
        } else {
          const num = item;
          const active = item === current_page;
          let label = `${translations['go-to-page']} ${num}`;

          if (active) label = `${translations.page} ${num}, ${translations['current-page']}`;

          const props = {
            href: '#',
            onClick: event => handlePaginationItemClick(event, num),
            className: getLinkClassNames(active),
            'aria-label': label,
            title: label,
          };

          if (active) props['aria-current'] = true;

          const object = (
            <li
              className={getListItemClassNames}
              key={`ac-pagination__item--${num}`}
              rel={`ac-pagination__item--${num}`}
            >
              <a {...props}>
                <div>
                  {!active && <AcRipple theme={THEMES.PITCH} size={SIZES.SMALL} simple />}
                  {num}
                </div>
              </a>
            </li>
          );

          result.push(object);
        }
      }

      result.unshift(
        <Fragment key={`ac-pagination__item--previous`}>{renderPreviousPageItem}</Fragment>
      );
      result.unshift(<Fragment key={`ac-pagination__item--first`}>{renderFirstPageItem}</Fragment>);

      result.push(<Fragment key={`ac-pagination__item--next`}>{renderNextPageItem}</Fragment>);
      result.push(<Fragment key={`ac-pagination__item--last`}>{renderLastPageItem}</Fragment>);

      return result;
    }, [
      range,
      current_page,
      total_count,
      getLinkClassNames,
      getListItemClassNames,
      renderFirstPageItem,
      renderPreviousPageItem,
      renderNextPageItem,
      renderLastPageItem,
    ]);

    const renderFromToResults = useMemo(() => {
      if (!showFromTo) return null;
      if (!AcIsSet(from) || !AcIsSet(to) || !AcIsSet(total_count)) return null;

      const content = `${translations.showing} <strong>${from}</strong> - <strong>${to}</strong> ${translations.of} <strong>${total_count}</strong> ${translations.results}`;

      return (
        <div
          className={getFromToClassNames}
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      );
    }, [showFromTo, from, to, total_count]);

    return (
      <div className={getMainClassNames} {...rest}>
        {renderFromToResults}
        <nav
          role={'navigation'}
          aria-label={`${translations['pagination-navigation']}`}
          className={getNavClassNames}
        >
          <ul className={getListClassNames}>{renderPaginationItems}</ul>
        </nav>
      </div>
    );
  }
);

AcPagination.displayName = 'AcPagination';
AcPagination.propTypes = Props;

export {AcPagination};
export default AcPagination;
